import { useEffect, useMemo, useRef, useState, type MutableRefObject } from 'react'
import { useFieldArray, useForm, type Control, type UseFormSetValue } from 'react-hook-form'
import { toast } from 'sonner'
import dayjs from 'dayjs'
import { useFlightLogGetForTripLeg, useFlightLogSubmit, useFlightLogUpsertDraft } from '@/lib/hooks'
import {
  generateFlightLogDocumentS3Key,
  uploadFileWithPresignedPost,
  uploadThumbnailBestEffort,
  useS3GetUploadPost,
} from '@/lib/hooks/s3'
import { diffClock, formatHmm, parseHmm } from '@/lib/flight-log-utils'
import { computeDayNight, resolveLegClocks, type LatLon } from '@@api/dayNight.shared'
import { isPilotRole } from '@@api/shared'
import {
  deriveFlightLogLifecycle,
  flightLogContentChanged,
  flightLogSectionOrder,
  flightLogSubmitSchema,
  sectionKeyForIssuePath,
  sortCrewByPosition,
  type FlightLogBroughtForward,
  type FlightLogBroughtForwardSource,
  type FlightLogSectionKey,
  type FlightLogSignOffPhase,
} from '@@api/flightLog.shared'
import {
  FIXED_ENGINE_COUNT,
  buildEmptyForm,
  buildFlightLogPayload,
  emptyCyclesCell,
  emptyHoursCell,
  emptyEctmEngine,
  emptyOilEngine,
  isUploadedDocument,
  legacyDayNight,
  resizeArray,
  type CrewDuty,
  type DayNight,
  type FlightLogDocumentField,
  type FlightLogFormData,
  type LegacyConditions,
  type LegacyOilEngine,
  type LegacyPilotSignOff,
  type NatureOfFlight,
} from './form'

// Surface a server-side rejection (e.g. "flight hasn't departed yet") verbatim;
// anything else (network, unexpected) gets the generic message.
function saveErrorMessage(error: unknown): string {
  if (
    typeof error === 'object' &&
    error !== null &&
    (error as { code?: unknown }).code === 'BAD_REQUEST' &&
    typeof (error as { message?: unknown }).message === 'string'
  ) {
    return (error as { message: string }).message
  }
  return 'Failed to save flight log'
}

// Shared prop bag threaded into every section component
export interface FlightLogSectionContext {
  control: Control<FlightLogFormData>
  values: FlightLogFormData
  setValue: UseFormSetValue<FlightLogFormData>
  readOnly: boolean
  fieldError: (path: string) => string | undefined
  // Crew rostered on the flight — the pool the Crew section assigns from
  legCrew: FlightLogLegCrewMember[]
}

export type FlightLogLegCrewMember = { crewId: string; name: string; crewRole: string | null }

interface UseFlightLogFormArgs {
  isOpen: boolean
  tripLegId: string
  // Force read-only. Independently of this, the form is read-only whenever the
  // server says the viewer is not a pilot crewed on the leg (viewer.canEdit).
  readOnly: boolean
  initialSection?: FlightLogSectionKey
  onClose: () => void
}

export function useFlightLogForm({
  isOpen,
  tripLegId,
  readOnly: forceReadOnly,
  initialSection,
  onClose,
}: UseFlightLogFormArgs) {
  const { data, isLoading, isFetching } = useFlightLogGetForTripLeg(isOpen ? tripLegId : undefined)
  // Only pilots assigned to this leg may fill in, sign and submit the log
  // (server enforces the same in upsertDraft/submit). Until the response is
  // in, treat the form as read-only so editing controls never flash.
  const canEdit = (data as { viewer?: { canEdit?: boolean } } | undefined)?.viewer?.canEdit === true
  const readOnly = forceReadOnly || !canEdit
  const upsertDraft = useFlightLogUpsertDraft()
  const submitLog = useFlightLogSubmit()
  const [saving, setSaving] = useState<false | 'draft' | 'submit'>(false)
  // All sections start collapsed; deep-links and validation failures open theirs
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({})
  // Field errors surface only after a failed post-landing save, then
  // live-update as the user fixes them
  const [showErrors, setShowErrors] = useState(false)

  const { control, reset, setValue, watch, getValues } = useForm<FlightLogFormData>({
    defaultValues: buildEmptyForm(FIXED_ENGINE_COUNT),
  })

  const approachArray = useFieldArray({ control, name: 'conditions.approaches' })
  const defectArray = useFieldArray({ control, name: 'defects.entries' })

  const existingLog = data?.flightLog
  const broughtForward = (data?.broughtForward ?? null) as FlightLogBroughtForward | null
  // Which flight the brought-forward totals came from (named in Hours/APU)
  const broughtForwardSource = (data?.broughtForwardSource ??
    null) as FlightLogBroughtForwardSource | null
  // Previous submitted log's next-inspection figures — offered as this log's
  // default behind a "same as previous" toggle.
  const previousInspection = (data?.previousInspection ??
    null) as FlightLogFormData['inspection'] | null
  const [keepPreviousInspection, setKeepPreviousInspectionState] = useState(false)
  // The nature-of-flight column has no UI in the redesign — a stored value is
  // carried through saves untouched.
  const natureRef = useRef<NatureOfFlight | null>(null)

  // Licence numbers from the flight's crew records — used to prefill the
  // sign-off blocks and backfill after duty changes
  const licenceByCrewId = useMemo(() => {
    const entries = (
      (data?.prefill?.crew ?? []) as { crewId: string; pilotLicenseNumber?: string | null }[]
    ).map((member) => [member.crewId, member.pilotLicenseNumber ?? ''] as const)
    return new Map(entries)
  }, [data])

  // Seed the form once per open session. Waits for the in-flight refetch to
  // settle so a just-invalidated cache (e.g. crew changed on the flight right
  // before opening) never seeds the form with stale prefill data.
  const seededRef = useRef(false)
  const [seeded, setSeeded] = useState(false)
  useEffect(() => {
    if (!isOpen) {
      seededRef.current = false
      setSeeded(false)
      return
    }
    if (seededRef.current || !data || isFetching) return
    seededRef.current = true
    setSeeded(true)

    const engineCount = FIXED_ENGINE_COUNT
    const base = buildEmptyForm(engineCount)
    natureRef.current = (existingLog?.natureOfFlight as NatureOfFlight | null) ?? null

    // Crew and PAX always mirror the flight itself — the log only owns each
    // member's duty and PF/PM. Duty guess by role: pilots → PIC, SIC, then
    // OTHER (3rd pilot); Engineer → FE; attendants/others → FA. When a stored
    // log exists, its saved duty/pfPm is merged back in by crewId so the list
    // stays in sync with later crew changes on the flight.
    const storedCrew =
      ((existingLog?.routeTimes as FlightLogFormData['routeTimes'] | null)?.crew ??
        []) as FlightLogFormData['routeTimes']['crew']
    let pilotIndex = 0
    const unorderedCrew = (data.prefill?.crew ?? []).map(
      (member: { crewId: string; name: string; crewRole: string | null }) => {
        let duty: CrewDuty
        let pfPm: 'PF' | 'PM' | null
        if (isPilotRole(member.crewRole)) {
          duty = pilotIndex === 0 ? 'PIC' : pilotIndex === 1 ? 'SIC' : 'OTHER'
          pfPm = pilotIndex === 0 ? 'PF' : 'PM'
          pilotIndex += 1
        } else if (member.crewRole === 'Engineer') {
          duty = 'FE'
          pfPm = null
        } else {
          duty = 'FA'
          pfPm = null
        }
        const stored = storedCrew.find((entry) => entry.crewId === member.crewId)
        const finalDuty = stored?.duty ?? duty
        // pfPm is derived from the position: PIC is the Pilot Flying, SIC the
        // Pilot Monitoring. Legacy logs that toggled SIC to PF collapse to PM.
        return {
          crewId: member.crewId,
          name: member.name,
          duty: finalDuty,
          pfPm: finalDuty === 'PIC' ? 'PF' : finalDuty === 'SIC' ? 'PM' : null,
        }
      }
    )
    // Fixed display order: PF → PM → augmented → FA → engineer
    const crew = sortCrewByPosition<FlightLogFormData['routeTimes']['crew'][number]>(
      unorderedCrew
    )

    const flightRouteFields = {
      from: data.prefill?.from ?? '',
      to: data.prefill?.to ?? '',
      paxCount: data.prefill?.paxCount ?? 0,
      crew,
    }

    // Captain sign-off phases target the PIC. Stored phases re-attach as-is;
    // the legacy per-pilot array maps its PIC entry onto preFlight.
    const pic = crew.find((member) => member.duty === 'PIC') ?? null
    const basePhase = (): FlightLogSignOffPhase => ({
      crewId: pic?.crewId ?? null,
      name: pic?.name ?? '',
      licenceNo: pic ? (licenceByCrewId.get(pic.crewId ?? '') ?? '') : '',
      signaturePng: null,
      signedAt: null,
    })
    const storedSignOff = (existingLog?.signOff ?? null) as {
      preFlight?: (FlightLogSignOffPhase & { name?: string }) | null
      postFlight?: FlightLogSignOffPhase | null
      pilots?: LegacyPilotSignOff[]
    } | null
    const normalizePhase = (
      stored: (Partial<FlightLogSignOffPhase> & LegacyPilotSignOff) | null | undefined
    ): FlightLogSignOffPhase => {
      const fallback = basePhase()
      // A signed phase is a record — re-attach it as stored. An unsigned one
      // always follows the current PIC (name + licence from the crew record).
      if (!stored?.signaturePng) return fallback
      // The licence is never a stored fact: it's read from the signer's crew
      // record every time, so a profile correction shows on old logs too. The
      // stored string only stands in when the signer is no longer on the leg.
      const signerId = stored.crewId ?? fallback.crewId
      return {
        crewId: signerId,
        name: stored.name || fallback.name,
        licenceNo: (signerId && licenceByCrewId.get(signerId)) || stored.licenceNo || '',
        signaturePng: stored.signaturePng,
        signedAt: stored.signedAt ?? null,
      }
    }
    const legacyPicEntry = storedSignOff?.pilots?.find(
      (entry) => entry.crewId != null && entry.crewId === pic?.crewId
    )
    const signOff: FlightLogFormData['signOff'] = {
      preFlight: normalizePhase(storedSignOff?.preFlight ?? legacyPicEntry),
      postFlight: normalizePhase(storedSignOff?.postFlight),
    }

    if (existingLog) {
      const stored = existingLog as Record<string, unknown>
      // Per-engine arrays are clamped to the fixed layout in case an older
      // draft was saved with a different engine count.
      const storedHours = { ...base.hours, ...(stored.hours as object | null) } as FlightLogFormData['hours']
      const storedOilRaw = (stored.oil ?? null) as {
        engines?: LegacyOilEngine[]
        apuUpliftQts?: number | null
        hydUpliftQts?: number | null
        psiDif?: number | null
      } | null
      const storedEctm = { ...base.ectm, ...(stored.ectm as object | null) } as FlightLogFormData['ectm']
      const storedConditions = (stored.conditions ?? null) as
        | (Partial<FlightLogFormData['conditions']> & LegacyConditions)
        | null
      reset({
        ...base,
        routeTimes: {
          ...base.routeTimes,
          ...(stored.routeTimes as object | null),
          ...flightRouteFields,
        },
        hours: {
          ...storedHours,
          engineCount,
          engines: resizeArray(storedHours.engines, engineCount, emptyHoursCell),
          cycles: {
            engines: resizeArray(storedHours.cycles?.engines, engineCount, emptyCyclesCell),
            apu: storedHours.cycles?.apu ?? emptyCyclesCell(),
            landings: storedHours.cycles?.landings ?? emptyCyclesCell(),
          },
        },
        fuel: { ...base.fuel, ...(stored.fuel as object | null) },
        conditions: {
          ...base.conditions,
          dayTime: storedConditions?.dayTime ?? base.conditions.dayTime,
          nightTime: storedConditions?.nightTime ?? base.conditions.nightTime,
          takeOff:
            storedConditions?.takeOff ??
            legacyDayNight(storedConditions?.dayTakeoffs, storedConditions?.nightTakeoffs),
          landing:
            storedConditions?.landing ??
            legacyDayNight(storedConditions?.dayLandings, storedConditions?.nightLandings),
          instrumentTime: storedConditions?.instrumentTime ?? null,
          approaches: storedConditions?.approaches ?? [],
        },
        deice: {
          ...base.deice,
          ...(stored.deice as object | null),
          stopTime: (stored.deice as { stopTime?: string | null } | null)?.stopTime ?? null,
        },
        oil: {
          engines: resizeArray(storedOilRaw?.engines, engineCount, emptyOilEngine).map((engine) => ({
            upliftQts: engine.upliftQts ?? null,
            remainingQts: engine.remainingQts ?? engine.postShutdownQts ?? null,
          })),
          apuUpliftQts: storedOilRaw?.apuUpliftQts ?? null,
          hydUpliftQts: storedOilRaw?.hydUpliftQts ?? null,
          psiDif: storedOilRaw?.psiDif ?? null,
        },
        ectm: {
          ...storedEctm,
          engines: resizeArray(storedEctm.engines, engineCount, emptyEctmEngine),
        },
        inspection: seedInspection(
          { ...base.inspection, ...(stored.inspection as object | null) },
          previousInspection
        ),
        signOff,
        defects: (() => {
          const merged = { ...base.defects, ...(stored.defects as object | null) } as FlightLogFormData['defects']
          // Normalize entries saved before the action/MEL fields existed
          return {
            ...merged,
            entries: (merged.entries ?? []).map((entry) => ({
              description: entry.description ?? '',
              action: entry.action ?? 'A',
              melRef: entry.melRef ?? null,
              actionNotes: entry.actionNotes ?? null,
            })),
          }
        })(),
        documents: (stored.documents as FlightLogFormData['documents'] | null) ?? [],
      })
      return
    }

    reset({
      ...base,
      routeTimes: {
        ...base.routeTimes,
        ...flightRouteFields,
      },
      signOff,
      inspection: seedInspection(base.inspection, previousInspection),
      hours: {
        ...base.hours,
        airframe: { broughtFwd: broughtForward?.airframe ?? '0:00', thisLeg: '0:00' },
        engines: resizeArray([], engineCount, emptyHoursCell).map((cell, i) => ({
          ...cell,
          broughtFwd: broughtForward?.engines[i] ?? '0:00',
        })),
        // APU run time is ground time, unrelated to flight time — starts blank
        apu: { broughtFwd: broughtForward?.apu ?? '0:00', thisLeg: '' },
        cycles: {
          engines: resizeArray([], engineCount, emptyCyclesCell).map((cell, i) => ({
            ...cell,
            broughtFwd: broughtForward?.cycles.engines[i] ?? 0,
          })),
          apu: { broughtFwd: broughtForward?.cycles.apu ?? 0, thisLeg: null },
          landings: { broughtFwd: broughtForward?.cycles.landings ?? 0, thisLeg: 1 },
        },
      },
    })
  }, [isOpen, data, isFetching, existingLog, broughtForward, previousInspection, licenceByCrewId, reset])

  // The "same as previous log" toggle starts on when this log's inspection
  // matches the previous one (freshly carried over, or saved unchanged).
  useEffect(() => {
    if (!seeded) return
    const current = getValues('inspection')
    setKeepPreviousInspectionState(
      previousInspection !== null && sameInspection(current, previousInspection)
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seeded])

  const setKeepPreviousInspection = (keep: boolean) => {
    setKeepPreviousInspectionState(keep)
    if (keep && previousInspection) {
      setValue('inspection', { ...previousInspection }, { shouldDirty: true })
    }
  }

  // ---- watched + derived values ----
  const values = watch()
  const computedBlockTime = useMemo(() => {
    const minutes = diffClock(values.routeTimes.offBlock, values.routeTimes.onBlock)
    return minutes !== null ? formatHmm(minutes) : null
  }, [values.routeTimes.offBlock, values.routeTimes.onBlock])
  const computedFlightTime = useMemo(() => {
    const minutes = diffClock(values.routeTimes.takeOff, values.routeTimes.landing)
    return minutes !== null ? formatHmm(minutes) : null
  }, [values.routeTimes.takeOff, values.routeTimes.landing])
  const fuelUsed =
    values.fuel.departTotalLbs !== null && values.fuel.arrivalTotalLbs !== null
      ? values.fuel.departTotalLbs - values.fuel.arrivalTotalLbs
      : null

  // Do this log's Brought Fwd cells equal the previous flight's New Totals?
  // A draft seeded before that flight was submitted (or hand-edited) won't
  // match, and the Hours/APU sections then offer a refresh.
  const broughtForwardMatches = useMemo(() => {
    if (!broughtForward) return true
    const h = values.hours
    const sameHmm = (a: string | null | undefined, b: string) =>
      parseHmm(a ?? null) === parseHmm(b)
    return (
      sameHmm(h.airframe.broughtFwd, broughtForward.airframe) &&
      sameHmm(h.apu.broughtFwd, broughtForward.apu) &&
      h.engines.every((cell, i) => sameHmm(cell.broughtFwd, broughtForward.engines[i] ?? '0:00')) &&
      (h.cycles.apu.broughtFwd ?? 0) === broughtForward.cycles.apu &&
      (h.cycles.landings.broughtFwd ?? 0) === broughtForward.cycles.landings &&
      h.cycles.engines.every(
        (cell, i) => (cell.broughtFwd ?? 0) === (broughtForward.cycles.engines[i] ?? 0)
      )
    )
  }, [values.hours, broughtForward])

  // Replace every Brought Fwd cell with the previous flight's New Total;
  // This Leg cells are left alone.
  const applyBroughtForward = () => {
    if (!broughtForward) return
    const opts = { shouldDirty: true } as const
    setValue('hours.airframe.broughtFwd', broughtForward.airframe, opts)
    setValue('hours.apu.broughtFwd', broughtForward.apu, opts)
    setValue('hours.cycles.apu.broughtFwd', broughtForward.cycles.apu, opts)
    setValue('hours.cycles.landings.broughtFwd', broughtForward.cycles.landings, opts)
    getValues('hours.engines').forEach((_, i) => {
      setValue(`hours.engines.${i}.broughtFwd`, broughtForward.engines[i] ?? '0:00', opts)
    })
    getValues('hours.cycles.engines').forEach((_, i) => {
      setValue(`hours.cycles.engines.${i}.broughtFwd`, broughtForward.cycles.engines[i] ?? 0, opts)
    })
  }

  // Block & flight time default from the clock rows (off/on block, take-off/
  // landing) into their editable fields — only empty values or the previous
  // auto value are overwritten, so a pilot's manual entry sticks.
  const lastAutoBlockRef = useRef<string | null>(null)
  const lastAutoFlightRef = useRef<string | null>(null)
  useEffect(() => {
    if (!seededRef.current) return
    const apply = (
      computed: string | null,
      name: 'routeTimes.blockTime' | 'routeTimes.flightTime',
      ref: MutableRefObject<string | null>
    ) => {
      if (!computed || computed === ref.current) return
      const current = getValues(name)
      if (!current || current === ref.current) {
        setValue(name, computed, { shouldDirty: true })
      }
      ref.current = computed
    }
    apply(computedBlockTime, 'routeTimes.blockTime', lastAutoBlockRef)
    apply(computedFlightTime, 'routeTimes.flightTime', lastAutoFlightRef)
  }, [computedBlockTime, computedFlightTime, getValues, setValue])

  // When flight time lands (auto or typed), default the hours "this leg"
  // cells to it — but only overwrite values that are still untouched ("0:00"
  // or the previous auto value), so manual edits stick.
  const flightTime = values.routeTimes.flightTime || null
  const lastAutoFlightTimeRef = useRef<string | null>(null)
  useEffect(() => {
    if (!flightTime || !seededRef.current) return
    const previousAuto = lastAutoFlightTimeRef.current
    if (flightTime === previousAuto) return
    lastAutoFlightTimeRef.current = flightTime

    const isUntouched = (value: string) => value === '0:00' || value === previousAuto
    const current = getValues()
    if (isUntouched(current.hours.airframe.thisLeg)) {
      setValue('hours.airframe.thisLeg', flightTime, { shouldDirty: true })
    }
    current.hours.engines.forEach((engine, i) => {
      if (isUntouched(engine.thisLeg)) {
        setValue(`hours.engines.${i}.thisLeg`, flightTime, { shouldDirty: true })
      }
    })
    // APU hours are deliberately not defaulted — unrelated to flight time.
  }, [flightTime, getValues, setValue])

  // ---- day / night from UTC clocks + airport positions ----
  // With the scheduled departure (date anchor), both airport positions, the
  // take-off clock and the flight time we can place the flight in time and
  // space and count day vs night minutes along the track (civil twilight).
  const legPrefill = data?.prefill as
    | {
        departureDateTime?: string | Date | null
        departureCoords?: LatLon | null
        arrivalCoords?: LatLon | null
      }
    | undefined
  const takeOffClock = values.routeTimes.takeOff
  const solar = useMemo(() => {
    const departure = legPrefill?.departureDateTime
    const from = legPrefill?.departureCoords
    const to = legPrefill?.arrivalCoords
    const flightMinutes = parseHmm(flightTime)
    if (!departure || !from || !to || !takeOffClock || flightMinutes === null) return null
    const clocks = resolveLegClocks({
      departureDateTime: new Date(departure),
      takeOff: takeOffClock,
      flightMinutes,
    })
    if (!clocks) return null
    return computeDayNight({ ...clocks, from, to })
  }, [legPrefill, takeOffClock, flightTime])

  // Take-off / landing phase default from the solar calculation. Only a
  // null value or the previous auto value is overwritten, so a pilot's
  // explicit choice sticks.
  const lastAutoTakeOffRef = useRef<DayNight | null>(null)
  const lastAutoLandingRef = useRef<DayNight | null>(null)
  useEffect(() => {
    if (!seededRef.current || !solar) return
    const current = getValues('conditions')
    if (
      (current.takeOff === null || current.takeOff === lastAutoTakeOffRef.current) &&
      current.takeOff !== solar.takeOffPhase
    ) {
      setValue('conditions.takeOff', solar.takeOffPhase, { shouldDirty: true })
    }
    if (
      (current.landing === null || current.landing === lastAutoLandingRef.current) &&
      current.landing !== solar.landingPhase
    ) {
      setValue('conditions.landing', solar.landingPhase, { shouldDirty: true })
    }
    lastAutoTakeOffRef.current = solar.takeOffPhase
    lastAutoLandingRef.current = solar.landingPhase
  }, [solar, getValues, setValue])

  // Day/night durations: from the solar split when available; otherwise
  // from the flight time when both phases agree (Day + Day → all day) and
  // mixed phases are left to the pilot. Only untouched values are
  // overwritten so manual splits stick.
  const takeOffPhase = values.conditions.takeOff
  const landingPhase = values.conditions.landing
  const lastAutoDayRef = useRef<string | null>(null)
  const lastAutoNightRef = useRef<string | null>(null)
  useEffect(() => {
    if (!seededRef.current || !flightTime) return
    let dayTarget: string
    let nightTarget: string
    if (solar) {
      dayTarget = formatHmm(solar.dayMinutes)
      nightTarget = formatHmm(solar.nightMinutes)
    } else {
      if (!takeOffPhase || !landingPhase || takeOffPhase !== landingPhase) return
      dayTarget = takeOffPhase === 'Day' ? flightTime : '0:00'
      nightTarget = takeOffPhase === 'Day' ? '0:00' : flightTime
    }
    const current = getValues('conditions')
    if (
      (current.dayTime === '0:00' || current.dayTime === lastAutoDayRef.current) &&
      current.dayTime !== dayTarget
    ) {
      setValue('conditions.dayTime', dayTarget, { shouldDirty: true })
    }
    if (
      (current.nightTime === '0:00' || current.nightTime === lastAutoNightRef.current) &&
      current.nightTime !== nightTarget
    ) {
      setValue('conditions.nightTime', nightTarget, { shouldDirty: true })
    }
    lastAutoDayRef.current = dayTarget
    lastAutoNightRef.current = nightTarget
  }, [flightTime, solar, takeOffPhase, landingPhase, getValues, setValue])

  // Instrument time defaults to the flight time (editable, Auto pill while
  // it still holds the auto value).
  const lastAutoInstrumentRef = useRef<string | null>(null)
  useEffect(() => {
    if (!seededRef.current || !flightTime) return
    if (flightTime === lastAutoInstrumentRef.current) return
    const current = getValues('conditions.instrumentTime')
    if (!current || current === lastAutoInstrumentRef.current) {
      setValue('conditions.instrumentTime', flightTime, { shouldDirty: true })
    }
    lastAutoInstrumentRef.current = flightTime
  }, [flightTime, getValues, setValue])

  const isSubmitted = existingLog?.status === 'Submitted'

  // Keep unsigned sign-off phases pointing at whoever currently holds PIC;
  // signed phases are frozen.
  const pic = values.routeTimes.crew.find((member) => member.duty === 'PIC') ?? null
  const picKey = pic ? `${pic.crewId}:${pic.name}` : ''
  useEffect(() => {
    if (!seededRef.current) return
    const crew = getValues('routeTimes.crew')
    const currentPic = crew.find((member) => member.duty === 'PIC') ?? null
    const dbLicence = currentPic ? (licenceByCrewId.get(currentPic.crewId ?? '') ?? '') : ''
    for (const key of ['preFlight', 'postFlight'] as const) {
      const phase = getValues(`signOff.${key}`)
      if (phase.signaturePng) continue
      const next: FlightLogSignOffPhase = {
        ...phase,
        crewId: currentPic?.crewId ?? null,
        name: currentPic?.name ?? '',
        // Licence is not editable — always the PIC's crew-record value
        licenceNo: dbLicence,
      }
      if (JSON.stringify(next) !== JSON.stringify(phase)) {
        setValue(`signOff.${key}`, next, { shouldDirty: true })
      }
    }
  }, [picKey, getValues, setValue, licenceByCrewId])

  const buildPayload = () =>
    buildFlightLogPayload(getValues(), {
      tripLegId,
      nature: natureRef.current,
      fuelUsed,
    })

  // Editing a signed-off log voids the captain's post-flight signature: the
  // first change clears it, so the edit only lands once the captain signs
  // again and resubmits (the API refuses it otherwise).
  const editedAfterSignOff = useMemo(
    () =>
      seeded &&
      !readOnly &&
      isSubmitted &&
      flightLogContentChanged(existingLog as Record<string, unknown>, buildPayload()),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [seeded, readOnly, isSubmitted, existingLog, values, fuelUsed]
  )
  useEffect(() => {
    if (!editedAfterSignOff) return
    const storedSignedAt =
      (existingLog?.signOff as { postFlight?: { signedAt?: string | null } | null } | null)
        ?.postFlight?.signedAt ?? null
    const postFlight = getValues('signOff.postFlight')
    if (!postFlight.signaturePng || postFlight.signedAt !== storedSignedAt) return
    setValue('signOff.postFlight.signaturePng', null, { shouldDirty: true })
    setValue('signOff.postFlight.signedAt', null, { shouldDirty: true })
  }, [editedAfterSignOff, existingLog, getValues, setValue])

  // Deep-link scroll: once the form is seeded, open + scroll to the requested
  // section (defect review). Otherwise every section starts collapsed.
  // Runs once per open session.
  useEffect(() => {
    if (!isOpen || !seeded) return
    if (!initialSection) return
    const section = initialSection
    setOpenSections((prev) => ({ ...prev, [section]: true }))
    requestAnimationFrame(() => {
      document
        .getElementById(`flight-log-section-${section}`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, seeded])

  // Re-validate live once a post-landing save failed, so borders/messages
  // clear as fields are filled in
  const submitErrors = useMemo(() => {
    if (!showErrors) return {} as Record<string, string>
    const { tripLegId: _legId, ...sections } = buildPayload()
    const parsed = flightLogSubmitSchema.safeParse(sections)
    if (parsed.success) return {} as Record<string, string>
    const map: Record<string, string> = {}
    for (const issue of parsed.error.issues) {
      const key = issue.path.join('.')
      if (!map[key]) map[key] = issue.message
    }
    return map
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showErrors, values, fuelUsed])

  const fieldError = (path: string): string | undefined => submitErrors[path]
  const sectionHasError = (key: string) =>
    Object.keys(submitErrors).some((path) => sectionKeyForIssuePath(path.split('.')) === key)

  const handleClose = () => {
    setOpenSections({})
    setShowErrors(false)
    setKeepPreviousInspectionState(false)
    onClose()
  }

  // Documents stage locally (File + object URL) until a save. Upload them
  // here and swap each for its {s3Key, fileName} pointer; on a mid-batch
  // failure the finished pointers are still committed so a retry never
  // re-uploads them.
  const getUploadPost = useS3GetUploadPost()
  const uploadPendingDocuments = async () => {
    const docs = getValues('documents')
    if (docs.every(isUploadedDocument)) return
    const resolved: FlightLogDocumentField[] = [...docs]
    try {
      for (let i = 0; i < docs.length; i++) {
        const doc = docs[i]
        if (isUploadedDocument(doc)) continue
        const s3Key = generateFlightLogDocumentS3Key(tripLegId, doc.fileName)
        await uploadFileWithPresignedPost(doc.file, s3Key, getUploadPost.mutateAsync)
        await uploadThumbnailBestEffort(doc.file, s3Key, getUploadPost.mutateAsync)
        URL.revokeObjectURL(doc.previewUrl)
        resolved[i] = { s3Key, fileName: doc.fileName }
      }
    } finally {
      setValue('documents', resolved, { shouldDirty: true })
    }
  }

  // "LFPG → EGLL · 16 Sep 2026" for save/submit toasts; empty when the
  // route hasn't been filled in yet.
  const flightLabel = (from: string, to: string) => {
    const departure = (
      data?.prefill as { departureDateTime?: string | Date | null } | undefined
    )?.departureDateTime
    return [
      from || to ? `${from} → ${to}` : null,
      departure ? dayjs(departure).format('D MMM YYYY') : null,
    ]
      .filter(Boolean)
      .join(' · ')
  }

  // Save draft: persists whatever is filled in, no validation UI. The API
  // never downgrades a Submitted log — on one, this just updates contents.
  // The drawer stays open (like Submit) so the pilot can keep working.
  const handleSaveDraft = async () => {
    setSaving('draft')
    try {
      await uploadPendingDocuments()
      const payload = buildPayload()
      await upsertDraft.mutateAsync(payload)
      const label = flightLabel(payload.routeTimes.from, payload.routeTimes.to)
      toast.success(label ? `Draft saved · ${label}` : 'Draft saved')
    } catch (error) {
      console.error('Error saving flight log draft:', error)
      toast.error(saveErrorMessage(error))
    } finally {
      setSaving(false)
    }
  }

  // Submit: a submit-complete log submits; an incomplete one is persisted as
  // a draft with the missing sections highlighted. Either way the drawer
  // stays open — after a successful submit the refetch flips the status pill
  // to "Signed off" so the pilot can review what was recorded.
  const handleSubmit = async () => {
    setSaving('submit')
    try {
      // Upload before building the payload so the new pointers are included
      await uploadPendingDocuments()
      const payload = buildPayload()
      const { tripLegId: _legId, ...sections } = payload
      const parsed = flightLogSubmitSchema.safeParse(sections)
      if (parsed.success) {
        await submitLog.mutateAsync({ tripLegId, ...parsed.data })
        setShowErrors(false)
        const { from, to } = parsed.data.routeTimes
        toast.success(
          `${isSubmitted ? 'Flight log updated' : 'Flight log submitted'} · ${flightLabel(from, to)}`
        )
        return
      }

      // A signed-off log can't be parked as a draft (the API refuses), so
      // only the missing sections are highlighted
      if (!isSubmitted) await upsertDraft.mutateAsync(payload)
      setShowErrors(true)
      const errorSections = new Set(
        parsed.error.issues.map((issue) => sectionKeyForIssuePath(issue.path))
      )
      setOpenSections((prev) => {
        const next = { ...prev }
        errorSections.forEach((key) => {
          next[key] = true
        })
        return next
      })
      const firstSection = flightLogSectionOrder.find((key) => errorSections.has(key))
      if (firstSection) {
        document
          .getElementById(`flight-log-section-${firstSection}`)
          ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
      toast(
        isSubmitted
          ? 'Complete the highlighted sections to sign off again'
          : 'Saved as draft — complete the highlighted sections to submit'
      )
    } catch (error) {
      console.error('Error saving flight log:', error)
      toast.error(saveErrorMessage(error))
    } finally {
      setSaving(false)
    }
  }

  const toggleSection = (key: string) => (open: boolean) =>
    setOpenSections((prev) => ({ ...prev, [key]: open }))

  // Trip-card lifecycle pill
  const lifecycle = deriveFlightLogLifecycle(existingLog)
  const statusPill: { variant: 'success' | 'warning' | 'info'; label: string } =
    lifecycle === 'complete'
      ? { variant: 'success', label: 'Signed off' }
      : lifecycle === 'awaitingLanding'
        ? { variant: 'info', label: 'In flight' }
        : lifecycle === 'inProgress' && values.signOff.preFlight.signaturePng
          ? { variant: 'warning', label: 'In progress' }
          : { variant: 'warning', label: 'Pending' }

  const departureDateTime = (
    data?.prefill as { departureDateTime?: string | Date | null } | undefined
  )?.departureDateTime
  // For the header's folio-scan action (scans are aircraft-scoped) and the
  // scan ↔ flight match check
  const aircraftId = (data?.prefill as { aircraftId?: string } | undefined)?.aircraftId
  const aircraftRegistration = (
    data?.prefill as { aircraftRegistration?: string | null } | undefined
  )?.aircraftRegistration

  const legCrew = useMemo(
    () =>
      (
        (data?.prefill as { crew?: FlightLogLegCrewMember[] } | undefined)?.crew ?? []
      ).map(({ crewId, name, crewRole }) => ({ crewId, name, crewRole })),
    [data]
  )

  const context: FlightLogSectionContext = {
    control,
    values,
    setValue,
    readOnly,
    fieldError,
    legCrew,
  }

  return {
    context,
    readOnly,
    isLoading,
    seeded,
    saving,
    openSections,
    toggleSection,
    sectionHasError,
    approachArray,
    defectArray,
    fuelUsed,
    pic,
    statusPill,
    isSubmitted,
    editedAfterSignOff,
    // Header badge: a saved log's sequential number (null until first save)
    logNumber: existingLog?.logNumber ?? null,
    lifecycle,
    departureDateTime,
    aircraftId,
    aircraftRegistration,
    handleClose,
    handleSaveDraft,
    handleSubmit,
    lastAutoDayRef,
    lastAutoNightRef,
    lastAutoTakeOffRef,
    lastAutoLandingRef,
    lastAutoInstrumentRef,
    solarAvailable: solar !== null,
    previousInspection,
    keepPreviousInspection,
    setKeepPreviousInspection,
    broughtForwardSource,
    broughtForwardMatches,
    applyBroughtForward,
  }
}

type InspectionForm = FlightLogFormData['inspection']

// Legacy drafts stored dueHours as a plain hour count; the field is now h:mm.
const normaliseDueHours = (value: unknown): string | null =>
  typeof value === 'number' ? formatHmm(value * 60) : typeof value === 'string' ? value : null

// A log with no inspection figures yet inherits the previous log's.
function seedInspection(
  stored: InspectionForm,
  previous: InspectionForm | null
): InspectionForm {
  const normalised = { ...stored, dueHours: normaliseDueHours(stored.dueHours) }
  if (previous && !normalised.dueHours && !normalised.dueDate) return { ...previous }
  return normalised
}

const sameInspection = (a: InspectionForm, b: InspectionForm) =>
  a.target === b.target && a.dueHours === b.dueHours && a.dueDate === b.dueDate
